import { useEffect, useMemo, useRef, useState } from 'react'
import { useSurvivorBoard } from '../../hooks/useLeagues'
import { useAuthStore } from '../../stores/authStore'
import Avatar from '../ui/Avatar'
import LoadingSpinner from '../ui/LoadingSpinner'
import SurvivorPickChip from './SurvivorPickChip'
import PlayerDetailModal from './PlayerDetailModal'
import GameCenterModal from '../picks/GameCenterModal'

const FOOTBALL_SPORTS = ['americanfootball_nfl', 'americanfootball_ncaaf']
const BASEBALL_SPORTS = ['baseball_mlb']

function pickVariant(id, count) {
  let hash = 0
  for (let i = 0; i < (id || '').length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i)
  return (Math.abs(hash) % count) + 1
}

function getTrophyImage(memberCount, sport, leagueId) {
  if (memberCount >= 14) {
    if (FOOTBALL_SPORTS.includes(sport)) return '/trophies/large-football.webp'
    if (BASEBALL_SPORTS.includes(sport)) return '/trophies/large-baseball.webp'
    return '/trophies/large-basketball.webp'
  }
  if (memberCount >= 9) return `/trophies/medium-${pickVariant(leagueId, 3)}.webp`
  if (memberCount >= 5) return `/trophies/small-${pickVariant(leagueId, 3)}.webp`
  return `/trophies/medal-${pickVariant(leagueId, 3)}.webp`
}

const PICK_STYLES = {
  survived: 'bg-correct/20 text-correct border border-correct/30',
  // Amber = still alive, but didn't actually win the period. Two ways in:
  //   survived_wrong — the pick LOST and all_eliminated_survive revived them.
  //   pushed         — there was never a result (postponed, or a draw).
  survived_wrong: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
  pushed: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
  eliminated: 'bg-incorrect/20 text-incorrect border border-incorrect/30',
  locked: 'bg-white/10 text-text-primary border border-white/20',
  pending: 'bg-white/10 text-text-primary border border-white/20',
  missed: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
}

// PickChain and MemberRow live at module scope on purpose.
//
// They used to be declared inside SurvivorStandings' body, which makes their
// function identity new on every render. React compares component types by
// identity, so it doesn't re-render those subtrees — it UNMOUNTS and remounts
// them. Every row's headshots were torn down and rebuilt on any state change
// in the parent, which is both the flicker and the reason the pick row kept
// snapping back to its right edge.
function PickChain({ member, weeks, nowMs, periodLabel, isDaily, sportKey, onOpenPlayer, onOpenGame }) {
  const scrollRef = useRef(null)
  const picks = member.picks || []

  // Build a chain: for every week that has been processed (missed_picks_processed
  // or past ends_at), include either the user's pick for that week, or a
  // "missed" placeholder if they have no pick. Weeks with no pick before
  // elimination = life lost.
  // `nowMs` is read once by the parent and passed down rather than sampled
  // here. Reading the clock during render gives every row a slightly
  // different "now", and React's purity rule rightly objects to it.
  const chain = (() => {
    const pickByWeekId = new Map(picks.map((p) => [p.league_week_id, p]))

    const out = []
    for (const week of weeks) {
      const weekEndedMs = new Date(week.ends_at).getTime()
      const isPast = week.missed_picks_processed || weekEndedMs < nowMs
      if (!isPast) continue

      const isPostElimination = !member.is_alive && member.eliminated_week != null && week.week_number > member.eliminated_week
      if (isPostElimination) continue

      const pick = pickByWeekId.get(week.id)
      if (pick) {
        out.push({ kind: 'pick', week, pick })
      } else if (week.missed_picks_processed) {
        // Week has been processed by missed-pick job and user has no pick → life lost
        out.push({ kind: 'missed', week })
      }
    }

    // Also include any picks for weeks that aren't yet past (e.g. current pending/locked)
    for (const p of picks) {
      if (!out.some((c) => c.kind === 'pick' && c.pick.id === p.id)) {
        out.push({ kind: 'pick', week: { week_number: p.league_weeks?.week_number }, pick: p })
      }
    }
    return out
  })()

  // Keep the newest pick in view. Only when the chain actually grows, and only
  // when there's something to scroll — the old inline `ref` callback ran this
  // on every attach, so it also yanked the row back whenever a manager had
  // deliberately scrolled left to look at an earlier week.
  useEffect(() => {
    const el = scrollRef.current
    if (el && el.scrollWidth > el.clientWidth) el.scrollLeft = el.scrollWidth
  }, [chain.length])

  if (!chain.length) return null

  return (
    <div
      ref={scrollRef}
      // overscroll-x-contain stops a trackpad gesture with any sideways
      // component from being swallowed by this row once the chain is long
      // enough to overflow.
      className="flex gap-2 overflow-x-auto overscroll-x-contain scrollbar-hide mt-2 pt-0.5"
    >
      {chain.map((item) => {
        if (item.kind === 'missed') {
          return (
            <SurvivorPickChip
              key={`missed-${item.week.id}`}
              missed
              weekNumber={item.week.week_number}
              periodLabel={periodLabel}
              isDaily={isDaily}
            />
          )
        }
        const p = item.pick
        return (
          <SurvivorPickChip
            key={p.id}
            pick={p}
            weekNumber={p.league_weeks?.week_number || item.week?.week_number}
            periodLabel={periodLabel}
            isDaily={isDaily}
            sportKey={sportKey}
            onOpenPlayer={onOpenPlayer}
            onOpenGame={onOpenGame}
          />
        )
      })}
    </div>
  )
}

function MemberRow({
  member, variant, scaleClass, periodLabel, onUserTap,
  weeks, nowMs, isDaily, sportKey, onOpenPlayer, onOpenGame,
}) {
  const isAlive = variant === 'alive'
  const isWinner = variant === 'winner'

  const borderClass = isWinner || isAlive
    ? 'border border-correct/50'
    : 'border border-incorrect/40'

  // No backdrop-blur here. These rows also carry a scale transform, and a
  // blurred backdrop stacked on a transformed layer is what made the row's
  // contents smear against their own border while the page scrolled. The tint
  // is 5% over an opaque page, so the blur was buying nothing to begin with.
  const bgClass = isWinner || isAlive ? 'bg-correct/5' : 'bg-incorrect/5'

  const rowSize = isAlive || isWinner ? 'px-4 py-4' : 'px-4 py-3'
  const avatarSize = isAlive || isWinner ? 'lg' : 'md'
  const nameSize = isAlive || isWinner ? 'text-base font-bold' : 'text-sm font-semibold'

  return (
    <div
      onClick={() => onUserTap?.(member.user_id)}
      className={`rounded-xl ${borderClass} ${bgClass} ${rowSize} ${(isAlive || isWinner) ? scaleClass : ''} cursor-pointer hover:brightness-110 transition-[filter] origin-center`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar user={member.users} size={avatarSize} />
          <div className="min-w-0">
            <span className={`${nameSize} text-text-primary truncate block`}>
              {member.users?.display_name || member.users?.username}
            </span>
            {isAlive && member.lives_remaining > 0 && (
              <span className="text-xs text-text-muted">
                {member.lives_remaining} {member.lives_remaining === 1 ? 'life' : 'lives'}
              </span>
            )}
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${
          isWinner || isAlive
            ? 'bg-correct/20 text-correct'
            : 'bg-incorrect/20 text-incorrect'
        }`}>
          {isWinner ? 'Champion' : isAlive ? 'Alive' : `Out ${periodLabel} ${member.eliminated_week}`}
        </span>
      </div>
      <PickChain
        member={member}
        weeks={weeks}
        nowMs={nowMs}
        periodLabel={periodLabel}
        isDaily={isDaily}
        sportKey={sportKey}
        onOpenPlayer={onOpenPlayer}
        onOpenGame={onOpenGame}
      />
    </div>
  )
}

export default function SurvivorStandings({ league, onUserTap }) {
  const { data: board, isLoading } = useSurvivorBoard(league.id)
  const session = useAuthStore((s) => s.session)
  const currentUserId = session?.user?.id
  // Tapping a pick opens that player's card, matching the picks row on the
  // Picks tab. Only touchdown-survivor picks carry a player_id; team picks,
  // locked picks and missed periods render inert.
  const [detailPlayer, setDetailPlayer] = useState(null)
  // Team picks open that game's Game Center. The chip stops propagation so
  // this doesn't fight MemberRow's own tap target, which opens the profile.
  const [detailGameId, setDetailGameId] = useState(null)
  // Sampled once per mount, not per render: every row then agrees on which
  // periods are "past", and render stays pure.
  const [nowMs] = useState(() => Date.now())
  const onOpenPlayer = setDetailPlayer
  const onOpenGame = setDetailGameId

  const isDaily = league.settings?.pick_frequency === 'daily'
  const periodLabel = isDaily ? 'Day' : 'Wk'

  const { alive, eliminated, winner, aliveCount } = useMemo(() => {
    if (!board?.members) return { alive: [], eliminated: [], winner: null, aliveCount: 0 }

    const aliveMembers = []
    const eliminatedMembers = []
    let winnerMember = null

    for (const m of board.members) {
      if (m.is_alive) {
        aliveMembers.push(m)
      } else {
        eliminatedMembers.push(m)
      }
    }

    // Current user first among alive
    aliveMembers.sort((a, b) => {
      if (a.user_id === currentUserId) return -1
      if (b.user_id === currentUserId) return 1
      return 0
    })

    // Eliminated: most recently eliminated first, first eliminated last
    eliminatedMembers.sort((a, b) => (b.eliminated_week || 0) - (a.eliminated_week || 0))

    // Winner detection
    if (board.survivor_winner) {
      winnerMember = aliveMembers.find((m) => m.user_id === board.survivor_winner.user_id) || null
    }

    return { alive: aliveMembers, eliminated: eliminatedMembers, winner: winnerMember, aliveCount: aliveMembers.length }
  }, [board, currentUserId])

  if (isLoading) return <LoadingSpinner />
  if (!board) return null

  // Scale factor for alive rows when competition narrows
  const aliveScale =
    aliveCount <= 2 ? 'scale-[1.04]'
    : aliveCount <= 3 ? 'scale-[1.02]'
    : aliveCount <= 4 ? 'scale-[1.01]'
    : ''

  const weeks = board.weeks || []
  const rowProps = {
    scaleClass: aliveScale,
    periodLabel,
    onUserTap,
    weeks,
    nowMs,
    isDaily,
    sportKey: league.sport,
    onOpenPlayer,
    onOpenGame,
  }

  return (
    <div>
      {/* Alive tier */}
      <div className="space-y-2">
        {alive.map((m) => (
          <MemberRow
            key={m.id}
            member={m}
            variant={winner?.user_id === m.user_id ? 'winner' : 'alive'}
            {...rowProps}
          />
        ))}
      </div>

      {/* Eliminated tier */}
      {eliminated.length > 0 && (
        <>
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-incorrect/20" />
            <span className="text-xs text-incorrect/60 font-semibold uppercase tracking-wider">Eliminated</span>
            <div className="flex-1 h-px bg-incorrect/20" />
          </div>
          <div className="space-y-2">
            {eliminated.map((m) => (
              <MemberRow key={m.id} member={m} variant="eliminated" {...rowProps} />
            ))}
          </div>
        </>
      )}

      {detailGameId && (
        <GameCenterModal gameId={detailGameId} onClose={() => setDetailGameId(null)} />
      )}

      {detailPlayer && (
        <PlayerDetailModal
          leagueId={league.id}
          playerId={detailPlayer}
          // Survivor has no fantasy scoring, so the Pts column and season
          // total are hidden — they'd be computed from a default preset
          // unrelated to how this contest actually scores.
          hideFantasyPoints
          onClose={() => setDetailPlayer(null)}
        />
      )}
    </div>
  )
}
